import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import TimeInput24 from '../components/TimeInput24.jsx';

// SplAdmin — admin creates SPL (overtime) authorizations directly into
// pay_overtime, no employee request/approval. Bulk over many employees in one
// date+window (e.g. all Gudang workers). Scheme-agnostic; the derivation reads
// these like any approved SPL. Source 'spkl' = SPKL created by Kepala Gudang.

async function getJSON(url) {
  const r = await fetch(url);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || ('HTTP ' + r.status));
  return d;
}
async function send(url, body, method = 'POST') {
  const r = await fetch(url, {
    method, headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  return { ok: r.ok, data: await r.json().catch(() => ({})) };
}

// Renders one of two tab panels chosen by props.tab: 'manual' (create form) or
// 'daftar' (the SPL list). Both panels share this component's state — creating an
// SPL in 'manual' refetches the 'daftar' list — so it must stay mounted across
// tab switches (the parent renders it unconditionally and toggles via props.tab).
export default function SplAdmin(props) {
  const [schemes] = createResource(() => getJSON('/api/schemes'));
  const [scheme, setScheme] = createSignal('');
  const [emps] = createResource(scheme, (sc) => getJSON('/api/employees' + (sc ? '?scheme=' + encodeURIComponent(sc) : '')));
  const [kw, setKw] = createSignal('');
  const [sel, setSel] = createStore({}); // nik -> bool
  const [date, setDate] = createSignal('');
  const [start, setStart] = createSignal('');
  const [end, setEnd] = createSignal('');
  const [source, setSource] = createSignal('admin');
  const [busy, setBusy] = createSignal(false);
  const [msg, setMsg] = createSignal(null);

  const [from, setFrom] = createSignal('');
  const [to, setTo] = createSignal('');
  const [list, { refetch: refetchList }] = createResource(
    () => `${from()}|${to()}`,
    () => getJSON(`/api/overtime?from=${from()}&to=${to()}`));

  const filteredEmps = createMemo(() => {
    const k = kw().trim().toLowerCase();
    return (emps() ?? []).filter((e) => e.active !== false &&
      (!k || (e.name || '').toLowerCase().includes(k) || (e.nik || '').toLowerCase().includes(k) || (e.department || '').toLowerCase().includes(k)));
  });
  const selectedCount = () => Object.values(sel).filter(Boolean).length;
  const selectAll = (on) => { for (const e of filteredEmps()) setSel(e.nik, on); };

  async function create() {
    const niks = Object.keys(sel).filter((n) => sel[n]);
    if (!date() || !start() || !end()) { setMsg({ ok: false, msg: 'Isi tanggal, jam mulai, dan jam selesai.' }); return; }
    if (niks.length === 0) { setMsg({ ok: false, msg: 'Pilih minimal satu karyawan.' }); return; }
    setBusy(true);
    const r = await send('/api/overtime', { niks, date: date(), start: start(), end: end(), source: source() });
    setBusy(false);
    if (r.ok) {
      setMsg({ ok: true, msg: `SPL dibuat: ${r.data.created}${r.data.skipped ? `, dilewati ${r.data.skipped} (sudah ada)` : ''}.` });
      setSel({}); refetchList();
    } else setMsg({ ok: false, msg: r.data.error });
  }
  async function del(id) {
    if (!confirm('Hapus SPL ini?')) return;
    const r = await send(`/api/overtime/${id}`, null, 'DELETE');
    if (r.ok) refetchList(); else setMsg({ ok: false, msg: r.data.error });
  }

  return (
    <Show when={props.tab === 'manual' || props.tab === 'daftar'}>
    <div class="card card-pad">
      <Show when={msg()}><div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin:0 0 10px">{msg().msg}</div></Show>

      {/* ── SPL Manual: create authorizations directly into pay_overtime ─── */}
      <Show when={props.tab === 'manual'}>
        <div class="ky-vars-head">
          <h3 class="kg-hist-title">SPL (dibuat admin)</h3>
          <span class="kg-meta-text" style="margin-left:8px">otorisasi lembur langsung — tanpa pengajuan karyawan</span>
        </div>
        <p class="ky-vars-note">
          Buat SPL untuk satu atau banyak karyawan sekaligus dalam satu tanggal &amp; jendela jam (mis.
          semua pekerja Gudang). Dibaca oleh derivasi lembur sama seperti SPL hasil persetujuan. Untuk
          Kepala TKBM pilih sumber <b>Kepala Gudang (SPKL)</b>. Jendela melewati tengah malam (mis.
          17:00–07:00) otomatis dihitung ke hari berikutnya.
        </p>

        <div class="lb-filter">
          <label class="kg-field"><span>Tanggal</span><input type="date" value={date()} onInput={(e) => setDate(e.currentTarget.value)} /></label>
          <label class="kg-field"><span>Jam mulai</span><TimeInput24 value={start()} onChange={setStart} /></label>
          <label class="kg-field"><span>Jam selesai</span><TimeInput24 value={end()} onChange={setEnd} /></label>
          <label class="kg-field"><span>Sumber</span>
            <select class="kg-select" value={source()} onInput={(e) => setSource(e.currentTarget.value)}>
              <option value="admin">Admin (SPL)</option>
              <option value="spkl">Kepala Gudang (SPKL)</option>
            </select>
          </label>
        </div>

        <div class="lb-filter">
          <label class="kg-field"><span>Skema</span>
            <select class="kg-select" value={scheme()} onInput={(e) => setScheme(e.currentTarget.value)}>
              <option value="">Semua skema</option>
              <For each={schemes() ?? []}>{(s) => <option value={s.code}>{s.code}</option>}</For>
            </select>
          </label>
          <label class="kg-field lb-search"><span>Cari karyawan</span>
            <input type="text" placeholder="nama / NIK / departemen" value={kw()} onInput={(e) => setKw(e.currentTarget.value)} /></label>
          <button class="btn ghost" onClick={() => selectAll(true)}>Pilih semua ({filteredEmps().length})</button>
          <button class="btn ghost" onClick={() => setSel({})}>Kosongkan</button>
        </div>

        <div class="pg-grid-wrap" style="max-height:240px">
          <table class="data">
            <tbody>
              <For each={filteredEmps()}>{(e) =>
                <tr>
                  <td style="width:34px"><input type="checkbox" checked={!!sel[e.nik]} onChange={() => setSel(e.nik, (v) => !v)} /></td>
                  <td><div class="kg-code">{e.nik}</div><div class="kg-sub">{e.name}</div></td>
                  <td>{e.department || <span class="vg-dim">—</span>}</td>
                  <td class="kg-meta-text">{e.scheme}</td>
                </tr>
              }</For>
              <Show when={emps() && filteredEmps().length === 0}><tr><td colspan="4" class="kg-meta-text">Tidak ada karyawan cocok.</td></tr></Show>
            </tbody>
          </table>
        </div>

        <div class="ab-rekap-foot">
          <span class="kg-meta-text">{selectedCount()} karyawan dipilih</span>
          <div class="spacer" />
          <button class="btn primary" disabled={busy()} onClick={create}>{busy() ? 'Membuat…' : 'Buat SPL'}</button>
        </div>
      </Show>

      {/* ── Daftar SPL: list of created authorizations ──────────────────── */}
      <Show when={props.tab === 'daftar'}>
        <div class="ky-vars-head">
          <h3 class="kg-hist-title">Daftar SPL</h3>
          <div class="spacer" />
          <button class="btn ghost" disabled={list.loading} onClick={refetchList}>{list.loading ? 'Memuat…' : 'Muat ulang'}</button>
        </div>
        <div class="lb-filter">
          <label class="kg-field"><span>Dari tanggal</span><input type="date" value={from()} onInput={(e) => setFrom(e.currentTarget.value)} /></label>
          <label class="kg-field"><span>Sampai tanggal</span><input type="date" value={to()} onInput={(e) => setTo(e.currentTarget.value)} /></label>
        </div>
        <div class="pg-grid-wrap">
          <table class="data">
            <thead><tr><th>Karyawan</th><th>Departemen</th><th>Tanggal</th><th>Jam</th><th>Sumber</th><th>Dibuat oleh</th><th></th></tr></thead>
            <tbody>
              <For each={list() ?? []}>{(r) =>
                <tr>
                  <td><div class="kg-code">{r.nik}</div><div class="kg-sub">{r.name}</div></td>
                  <td>{r.department || <span class="vg-dim">—</span>}</td>
                  <td class="ky-nik">{r.workDate}</td>
                  <td class="ky-nik">{r.start}–{r.end}{r.overnight ? ' (+1)' : ''}</td>
                  <td><Show when={r.source} fallback={<span class="vg-dim">—</span>}><span class="badge info">{r.source}</span></Show></td>
                  <td class="kg-meta-text">{r.createdBy || (r.legacyNo ? 'impor ' + r.legacyNo : '—')}</td>
                  <td class="num"><button class="btn ghost" onClick={() => del(r.id)}>Hapus</button></td>
                </tr>
              }</For>
              <Show when={list() && list().length === 0}><tr><td colspan="7" class="kg-meta-text">Tidak ada SPL pada rentang ini.</td></tr></Show>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
    </Show>
  );
}
